import _typeof from "@babel/runtime/helpers/esm/typeof";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != _typeof(e) && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { NIKE_SHOP_ROOT_ID } from '@nike/shop-public-constants';
// import crcCodes from '@nike/ux-tread-crc-code/lib/buy/carts/v2/crcCodes'; // TODO MYBAG-2369 uncomment if we are supposed to do this on every page (we might have to for stale promos/pdp/or navigate away without clearing?)
import { getOptions as getFeatureFlagOptions } from '@nike/shop-apis/query-hooks/use-feature-flags';
import getFetchIdentityOptions from '@nike/shop-apis/identity/web-shell-client/get-options';
import getFetchAdobeTargetOptions from '@nike/shop-apis/adobe-target/get-options';
import getIsAdobeTargetQueryEnabled from '@nike/shop-apis/adobe-target/get-is-query-enabled';
import initializeWebShellIdentity from './initialize-web-shell-identity';
import observeCartStore from './observe-cart-store';
import { MINI_BAG_AT_LOCATION_NAME } from "../../components/item-added-modals/cart/mini-bag-analytics";
import NikeShop from "../nike-shop.js";
import getIsPageCart from "../../utils/get-is-page-cart";

/**
 * @module bootstrapNikeShop
 * @file bootstrap-nike-shop.js
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ARCHITECTURE NOTE: Dependency Injection Pattern
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * This module follows an explicit dependency injection pattern for timing safety,
 * testability, and refactoring ease.
 *
 * ## Key Principles
 *
 * 1. **Create dependencies first**: `queryClient` and `shopInstance` are
 *    instantiated at the top of bootstrapNikeShop before being used.
 *
 * 2. **Pass explicitly, don't rely on globals**: Setup functions receive
 *    dependencies as parameters rather than accessing `window.NikeShop` directly.
 *    This guarantees the dependency exists when the function executes.
 *
 * 3. **Execution order is visible**: The data flow is explicit in the code:
 *
 *    queryClient (created/provided)
 *         ↓
 *    shopInstance (created, exposed on window.NikeShop)
 *         ↓
 *    Setup functions receive { queryClient, shopInstance } as needed
 *
 * ## Why This Matters
 *
 * - **Timing Safety**: While `window.NikeShop` is set synchronously, relying on
 *   globals creates implicit coupling. Explicit injection guarantees existence.
 *
 * - **Testability**: Setup functions can be unit tested with mock dependencies
 *   without needing to set up global state.
 *
 * - **Refactoring**: Dependencies are traceable through function signatures.
 *   Changes are less likely to break silently.
 *
 * ## Example
 *
 * ```js
 * // ✅ Good: Dependencies passed explicitly
 * observeCartStore({ queryClient, shopInstance });
 *
 * // ❌ Avoid: Setup functions accessing window.NikeShop internally
 * observeCartStore(); // function internally uses window.NikeShop
 * ```
 *
 */

var LazyShopApp = /*#__PURE__*/React.lazy(function () {
  return /* webpackChunkName: ShopApp */Promise.resolve().then(function () {
    return _interopRequireWildcard(require("../../components/shop-app/shop-app"));
  });
});

/**
 * Creates a NikeShop instance and exposes it globally on window.NikeShop
 * @param {object} params - Configuration options
 * @param {QueryClient} params.queryClient - React Query client instance
 * @returns {NikeShop} The created NikeShop instance
 */
function createAndExposeNikeShop(_ref) {
  var queryClient = _ref.queryClient;
  var shopInstance = new NikeShop({
    queryClient: queryClient
  });
  Object.defineProperty(window, 'NikeShop', {
    value: shopInstance,
    configurable: true
  });
  return shopInstance;
}

/**
 * @name window.initNikeShop
 * @memberof shop-components
 * @param {object} params - named parameters
 * @param {Array.<string>} [params.errorsToExclude=['D3D734B6']] - an array of unique error crc codes to exclude, ex. codes via ux-tread-crc-code/lib/buy/carts/v2/crcCodes
 * @param {Array.<string>} [params.warningsToIncludeInDialog=[]] - an array of unique warning crc codes to include as errors
 * @param {QueryClient} [params.queryClient] - React Query client instance
 * @param {Function} [params.onCartUpdate] - Optional callback function called when cart updates
 * @returns {NikeShop} - singleton of NikeShop which is also exposed globally already as window.NikeShop
 *
 * @example
 * <caption>
 * #### Basic instantiation of nike-shop-client within first client file of an app.
 * </caption>
 * // client.prod.js
 * window.initNikeShop();
 *
 * @example
 * <caption>
 * #### Setting up html response and script tag
 * Our entry point installation is a script tag and an empty div added to the html response vs installation of node modules.
 * The div will be used for the NikeShop MFE to render and/or portal through.
 * In the future this might change to an edge-side includes tag but for now we'll simplify to this portion which you will not have to touch much in the long run.
 * - Place div using id from shop-public-constants module below the react app root div
 *   - the shop-public-constants package is purposefully decoupled to avoid requiring version updates and will never break as they will be only constants for public usage
 * - Directly following the react, react-dom and any polyfill scripts, add a script tag with the src value set to https://www.nike.com/static/experience/shop-components/public/nike-shop-client.js
 *   - Note that there is no version in the script src as we webpack to a tiny entry point file that should never change. It has a shorter cache time and the remainder of the code required will be dynamically loaded, with chunks only changing hashes based on which files were touched for changes.
 *     This lets us deploy changes independently without requiring consumers to bump versions in their code base.
 *
 * </caption>
 * // example of renderer.js portion as we understand our eco-system today
 * import { NIKE_SHOP_ROOT_ID } from "@nike/shop-public-constants";
 *
 * // in a function where you might be returning a string that creates the html response today
 * function htmlStringRenderer(params) {
 * // ...
 *  return `
 *     <div id="react-root">
 *       ${appString}
 *     </div>
 *     <div id="${NIKE_SHOP_ROOT_ID}"></div>
 *
 *     <script src="${VENDOR_SCRIPTS.reactScript}"></script>
 *     <script src="${VENDOR_SCRIPTS.reactDomScript}"></script>
 *     <script src="${VENDOR_SCRIPTS.intersectionObserverPolyfill}"></script>
 *     <script src="https://www.nike.com/static/experience/shop-components/public/nike-shop-client.js"></script>
 *   `;
 *    // ...
 * }
 *
 */
export default function bootstrapNikeShop() {
  var _window$webShellClien;
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$errorsToExclude = _ref2.errorsToExclude,
    errorsToExclude = _ref2$errorsToExclude === void 0 ? ['D3D734B6', 'D12EE4E6', '1E17F469', '9E64809B', 'F05351CA', 'AA33FB74'] : _ref2$errorsToExclude,
    _ref2$warningsToInclu = _ref2.warningsToIncludeInDialog,
    warningsToIncludeInDialog = _ref2$warningsToInclu === void 0 ? [] : _ref2$warningsToInclu,
    queryClient = _ref2.queryClient,
    onCartUpdate = _ref2.onCartUpdate;
  // NOTE - instead of pulling in the entire library, including this crcCode by default for empty errors. Should be manageable for us and make implementation easier for userland
  var isCartPage = getIsPageCart();
  // NOTE Multiple CRC codes are currently inlined here; if this list grows significantly or we lazy load CRCs we should move these to shared constants.
  var finalErrorsToExclude = [].concat(_toConsumableArray(isCartPage ? [].concat(_toConsumableArray(errorsToExclude), ['7E977FCE']) // Item does not have available inventory
  : [].concat(_toConsumableArray(errorsToExclude), ['36A1E761'])), [
  // invalid postal code in pdp foff request
  '23A6EE7E' // buy/carts/v2, Unsupported Country. Happens on load in non supported countries.
  ]);
  var startTime = Date.now();
  // NOTE - This function should fire ONLY once per app load.

  // Permit sharing query client with cart/faves app. Outside apps should inquire with cart team before syncing their query client with our own
  if (!queryClient) {
    // Lazy load the query client only when needed
    queryClient = require('@nike/shop-apis/utils/query-client')["default"];
  }

  // fetch feature flags here so we can access them in get(), since it's sync
  queryClient.prefetchQuery(getFeatureFlagOptions());

  // Warm the identity cache so getCheckoutHref has data before any modal renders
  if ((_window$webShellClien = window.webShellClient) !== null && _window$webShellClien !== void 0 && _window$webShellClien.identity) {
    queryClient.prefetchQuery(getFetchIdentityOptions());
  }

  // Create shopInstance and expose it globally on window.NikeShop
  var shopInstance = createAndExposeNikeShop({
    queryClient: queryClient
  });

  // Initialize web shell client identity and user
  initializeWebShellIdentity({
    startTime: startTime
  });

  // Set up cart observation
  observeCartStore({
    onCartUpdateCallback: onCartUpdate,
    queryClient: queryClient,
    shopInstance: shopInstance
  });
  var container = document.getElementById(NIKE_SHOP_ROOT_ID);
  var shopApp = /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(LazyShopApp, {
    queryClient: queryClient,
    errorsToExclude: finalErrorsToExclude,
    warningsToInclude: warningsToIncludeInDialog
  }));
  if (typeof ReactDOM.createRoot === 'function') {
    ReactDOM.createRoot(container).render(shopApp);
  } else {
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(shopApp, container);
  }

  // Warm the Adobe Target cache so A/B test data is ready before components render
  if (getIsAdobeTargetQueryEnabled()) {
    queryClient.prefetchQuery(getFetchAdobeTargetOptions({
      locationName: MINI_BAG_AT_LOCATION_NAME
    }));
  }
  return shopInstance;
}

// NOTE example of listener
// window.addEventListener(CART_COUNT_UPDATED_EVENT_NAME, evt =>
//   console.log('CARTCOUNTEVENT received', evt),
// );